import * as React from "react";
import { Box } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import type { ColumnsToken } from "../../components/ui/box-classes";
import { cva, type VariantProps } from "../../lib/cva";
import { useThemeMode } from "../../components/theme-provider";
import {
  colorLabel,
  NO_COLOR,
  PICKER_COLORS,
  type Color,
  type NoColor,
} from "../../lib/colors";
import type { AccentColor } from "../../lib/theme-appearance";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

// Every value is a literal — Tailwind's scanner cannot see composed names.
export const colorSwatchVariants = cva("", {
  variants: {
    size: {
      xs: "size-2",
      sm: "size-3",
      md: "size-4",
      lg: "size-5",
      xl: "size-6",
    },
    rounded: {
      none: "rounded-none",
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      full: "rounded-full",
    },
    /** Draw a selection ring around the swatch. */
    selected: {
      true: "ring-2 ring-ring ring-offset-2 ring-offset-background",
    },
  },
  defaultVariants: {
    size: "md",
    rounded: "sm",
  },
});

export type ColorSwatchSize = NonNullable<
  VariantProps<typeof colorSwatchVariants>["size"]
>;
export type ColorSwatchRounded = NonNullable<
  VariantProps<typeof colorSwatchVariants>["rounded"]
>;

type ColorSwatchSource =
  | {
      /** Palette or semantic color token. */
      color: Color;
      accent?: never;
    }
  | {
      color?: never;
      /**
       * An accent by name, painted from that accent's own `--primary` — which
       * is how a picker shows a theme it has not applied. `"none"` is the
       * shipped primary, so "no accent" is a swatch like any other rather than
       * a blank.
       *
       * This is a second source, not a second component: the box, the sizes and
       * the selection ring are the same, only where the fill comes from differs.
       */
      accent: AccentColor | "none";
    };

export type ColorSwatchProps = ColorSwatchSource &
  Omittable<{
    size?: ColorSwatchSize;
    rounded?: ColorSwatchRounded;
    /** Draw a selection ring around the swatch. */
    selected?: boolean;
    /** Click handler — renders the swatch as a real button when provided. */
    onClick?: () => void;
    "aria-label"?: string;
    className?: string;
  }>;

/**
 * A colored square or dot. Status markers, category indicators, and the
 * per-color cells of `ColorPalette`.
 */
export const ColorSwatch = React.memo(function ColorSwatch({
  color,
  accent,
  size,
  rounded,
  selected,
  onClick,
  className,
  ...props
}: ColorSwatchProps) {
  const classes = cn(
    colorSwatchVariants({ size, rounded, selected }),
    // `bg-primary` under a `data-accent` reads that accent's own block, which
    // is written unscoped for exactly this: a swatch paints a theme the
    // document has not adopted.
    accent === undefined ? "bg-c" : "bg-primary",
    className,
  );

  if (onClick) {
    return (
      <Box
        as="button"
        className={classes}
        data-color={color}
        data-accent={accent}
        data-slot="color-swatch"
        onClick={onClick}
        {...props}
      />
    );
  }

  return (
    <Box
      className={classes}
      data-color={color}
      data-accent={accent}
      data-slot="color-swatch"
      {...props}
    />
  );
});

/** Shared swatch chrome: grow + foreground ring on hover, foreground border
 *  when selected — the fill (or glyph) IS the datum, so feedback lives on the
 *  silhouette. */
const PALETTE_SWATCH_INTERACTION =
  // A 2px BORDER on a 24px dot eats 4px of the fill and leaves the paint edge on
// a half pixel, so the swatch reads soft and a shade off its own colour. An
// INSET ring sits inside the same radius without shrinking the box: the fill
// stays whole and the edge lands on the grid. The resting halo is gone for the
// same reason the appearance cards' was — three concentric edges inside 24
// pixels is what reads as blur.
  "transition-[transform,box-shadow] duration-fast motion-safe:hover:scale-110 hover:inset-ring-2 hover:inset-ring-foreground/40";

// `noColor` is a type-level discriminant, not just a flag: only pickers that
// opt in ever receive (or must handle) the NO_COLOR selection, so consumers
// whose data cannot be colorless (stage/icon pickers) keep their plain
// `Color` contract untouched.
type ColorPaletteSelection =
  | {
      /** Currently selected color. */
      color: Color;
      /** Called when a swatch is clicked. */
      onColorChange: (color: Color) => void;
      noColor?: false;
    }
  | {
      /** Currently selected color, or the explicit "no color" choice. */
      color: Color | NoColor;
      /** Called when a color swatch (or the "No color" one) is clicked. */
      onColorChange: (color: Color | NoColor) => void;
      /**
       * Append a trailing "No color" swatch (slash-circle): the item keeps
       * NO palette color and renders its neutral treatment. Never part of
       * random color suggestion — an explicit user pick only.
       */
      noColor: true;
    };

export type ColorPaletteProps = ColorPaletteSelection &
  Omittable<{
    /**
     * Lay the swatches out as a fixed grid of N tracks instead of a wrapping
     * row. A wrap has to survive its container's border and subpixel
     * rounding — a row sized to fit every {@link PICKER_COLORS} swatch on one
     * line breaks into two — so any host that cares where the rows break
     * declares the track count and cannot be argued with.
     */
    columns?: ColumnsToken;
  }>;

/** The curated picker palette ({@link PICKER_COLORS} membership and order) as
 *  a wrapped grid of pickable round swatches. Stored colors outside the
 *  curated set still render everywhere; they are just no longer offered. */
export const ColorPalette = React.memo(function ColorPalette(
  props: ColorPaletteProps,
) {
  const { color, columns, onColorChange } = props;
  const mode = useThemeMode();
  return (
    <Box
      // A column count lays the block out, and the block is content-sized:
      // 1fr tracks would spread the dots across a wide host (a card-width
      // popup, a phone sheet) while the same palette sits tight in a
      // content-width popup. One 24/4 rhythm everywhere — a host with room
      // places the block, never spaces the swatches.
      className={columns === undefined ? undefined : "w-fit"}
      data-slot="color-palette"
      gap="xs"
      {...(columns === undefined
        ? { direction: "row" as const, wrap: true }
        : { columns })}
    >
      {PICKER_COLORS.map((c) => (
        <Tooltip key={c}>
          <TooltipTrigger
            render={
              <Box rounded="full"
                as="button"
                aria-label={colorLabel(c, mode)}
                aria-pressed={c === color}
                className={cn(
                  "size-6 bg-c",
                  PALETTE_SWATCH_INTERACTION,
                  c === color && "inset-ring-2 inset-ring-foreground",
                )}
                data-color={c}
                data-slot="color-palette-swatch"
                onClick={() => onColorChange(c)}
              />
            }
          />
          <TooltipPopup>{colorLabel(c, mode)}</TooltipPopup>
        </Tooltip>
      ))}
      {props.noColor ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Box rounded="full"
                as="button"
                align="center"
                justify="center"
                aria-label="No color"
                aria-pressed={color === NO_COLOR}
                className={cn(
                  "size-6",
                  PALETTE_SWATCH_INTERACTION,
                  color === NO_COLOR && "inset-ring-2 inset-ring-foreground",
                )}
                data-slot="color-palette-swatch"
                onClick={() => props.onColorChange(NO_COLOR)}
              />
            }
          >
            {/* lg (20px) so the slash-circle's ring reads as a peer of the
                24px dots — a smaller step looks like a different control,
                not another swatch. */}
            <Icon name="circle-slash" size="lg" color="muted" />
          </TooltipTrigger>
          <TooltipPopup>No color</TooltipPopup>
        </Tooltip>
      ) : null}
    </Box>
  );
});
